/**
 * The drums.
 *
 * Every entry is a recipe, and every recipe is built the way the real instrument is built: a
 * kick is a beater on a tuned shell, a snare is wires under a drum, a hi-hat is two pieces of
 * inharmonic metal. The numbers below are the ones that matter — the pitch drop on a kick,
 * the burst spacing on a clap, the high-pass corner on a hat — and they were set by rendering
 * and measuring, not by copying a plausible-looking default. They are the same numbers
 * `DrumLibrary.swift` carries, so a kit generated in the browser is the kit the phone makes.
 */

import { makeSynthEnvelope, makeSynthFilter, makeSynthPitchEnvelope } from '../synth';
import { libraryLayer, librarySound, type LibrarySound } from './model';
import { bodyPatch, effect, libraryChain, noisePatch, tonePatch, transientPatch } from './patch';

// MARK: - Kicks

export const kicks: LibrarySound[] = [
  /**
   * The long sine kick a trap beat is built on: a 24-semitone drop over 45 ms and then
   * nothing but sub. The click is deliberately quiet — this kick is felt, not heard.
   */
  librarySound(
    'kick-808-sub',
    '808 Sub Kick',
    'kick',
    'Deep 808-style kick. A short click, then a long low tone that falls away slowly.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-808-sub',
          slot: 0,
          frequency: 47,
          dropSemitones: 24,
          dropSeconds: 0.045,
          decay: 0.42,
          lowPass: 200,
          lowPassOctaves: 4.2,
          drive: 0.3,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-808-sub', slot: 1, highPass: 3000, decay: 0.006 }), { gainDB: -15 }),
    ],
    { tags: ['drums', '808', 'sub', 'trap'] },
  ),

  /**
   * Shorter and angrier than the 808, with the drive doing most of the work. The post
   * distortion is what makes it survive being played through a phone speaker.
   */
  librarySound(
    'kick-trap',
    'Trap Kick',
    'kick',
    'Trap kick. Hard click, fast pitch drop, saturated low end.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-trap',
          slot: 0,
          frequency: 52,
          dropSemitones: 34,
          dropSeconds: 0.028,
          decay: 0.3,
          lowPass: 260,
          lowPassOctaves: 4.4,
          drive: 0.45,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-trap', slot: 1, highPass: 3500, decay: 0.005 }), { gainDB: -12 }),
    ],
    {
      tags: ['drums', 'trap', 'punchy'],
      post: libraryChain('kick-trap', [
        effect('distortion', { drive: 0.28, tone: 0.42, character: 1, mix: 0.5 }),
      ]),
    },
  ),

  /**
   * Four-to-the-floor: quick, mid-forward, and compressed so every one of them lands at
   * exactly the same level.
   */
  librarySound(
    'kick-house',
    'House Kick',
    'kick',
    'House kick. Tight and punchy with a clear beater click on the front.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-house',
          slot: 0,
          frequency: 55,
          dropSemitones: 30,
          dropSeconds: 0.02,
          decay: 0.2,
          curve: 0.7,
          lowPass: 300,
          lowPassOctaves: 4.0,
          lowPassDecay: 0.02,
          drive: 0.3,
        }),
      ),
      libraryLayer(
        transientPatch({ slug: 'kick-house', slot: 1, highPass: 4000, decay: 0.004, toneHz: 1800, toneLevel: 0.4 }),
        { gainDB: -9 },
      ),
    ],
    {
      tags: ['drums', 'house', 'punchy'],
      // Gentle and slow: the compressor is here to even the body out, and a threshold low
      // enough to catch the transient would flatten the beater instead — measured at −16 dB /
      // 4:1 it took 9 dB off the whole drum and left only the click intact.
      post: libraryChain('kick-house', [
        effect('compressor', { threshold: -7, ratio: 2.2, attack: 0.006, release: 0.09, makeup: 2 }),
      ]),
    },
  ),

  /**
   * Sampled-off-a-record dust: the bit crusher and the rate reduction are doing what an old
   * sampler's converters did, and the low-pass is the tape it came off.
   */
  librarySound(
    'kick-lofi',
    'Lo-Fi Kick',
    'kick',
    'Lo-fi kick. Soft and dusty, like it was sampled from an old record.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-lofi',
          slot: 0,
          frequency: 48,
          dropSemitones: 22,
          dropSeconds: 0.05,
          decay: 0.36,
          curve: 0.5,
          lowPass: 180,
          lowPassOctaves: 3.2,
          drive: 0.2,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-lofi', slot: 1, highPass: 1200, decay: 0.01 }), { gainDB: -16 }),
    ],
    {
      tags: ['drums', 'lo-fi', 'dusty'],
      post: libraryChain('kick-lofi', [
        effect('bitCrush', { bits: 9, mix: 0.55 }),
        effect('sampleRateReduce', { rate: 11025, mix: 0.5 }),
        effect('lowPass', { frequency: 6500, resonance: 0 }),
      ]),
    },
  ),

  librarySound(
    'kick-distorted',
    'Distorted Kick',
    'kick',
    'Distorted kick. Heavily overdriven, aggressive and broad.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-distorted',
          slot: 0,
          frequency: 50,
          dropSemitones: 30,
          dropSeconds: 0.03,
          decay: 0.32,
          lowPass: 400,
          lowPassOctaves: 3.8,
          drive: 0.7,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-distorted', slot: 1, highPass: 3000, decay: 0.006 }), { gainDB: -8 }),
    ],
    {
      tags: ['drums', 'distorted', 'hard'],
      post: libraryChain('kick-distorted', [
        effect('distortion', { drive: 0.55, tone: 0.45, character: 2, mix: 0.8 }),
        effect('lowPass', { frequency: 9000, resonance: 0 }),
      ]),
    },
  ),

  librarySound(
    'kick-tight',
    'Tight Kick',
    'kick',
    'Tight kick. Very short and dry, for fast patterns.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-tight',
          slot: 0,
          frequency: 58,
          dropSemitones: 30,
          dropSeconds: 0.018,
          decay: 0.2,
          release: 0.02,
          curve: 0.75,
          lowPass: 320,
          lowPassOctaves: 4.2,
          lowPassDecay: 0.015,
          drive: 0.35,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-tight', slot: 1, highPass: 4200, decay: 0.0035 }), { gainDB: -10 }),
    ],
    { tags: ['drums', 'tight', 'short'] },
  ),

  /**
   * Nearly all sub and nearly no click, for the last kick of a bar where the low end has to
   * hang over into the next one.
   */
  librarySound(
    'kick-sub-long',
    'Long Sub Kick',
    'kick',
    'Long sub kick. Almost pure low tone, decaying over most of a second.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-sub-long',
          slot: 0,
          frequency: 42,
          dropSemitones: 18,
          dropSeconds: 0.06,
          decay: 0.68,
          curve: 0.45,
          lowPass: 160,
          lowPassOctaves: 3.4,
          drive: 0.2,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-sub-long', slot: 1, highPass: 2500, decay: 0.005 }), { gainDB: -18 }),
    ],
    { tags: ['drums', 'sub', 'long'] },
  ),

  /**
   * Three explicit layers — snap, knock, sub — because that is how a kick is built in a
   * sampler: the click sells the timing, the knock sells the weight on a small speaker, and
   * the sub is what a subwoofer hears.
   */
  librarySound(
    'kick-click-body',
    'Click + Body Kick',
    'kick',
    'Layered kick. A sharp click, a mid knock and a deep body stacked together.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-click-body',
          slot: 0,
          frequency: 50,
          dropSemitones: 26,
          dropSeconds: 0.035,
          decay: 0.34,
          lowPass: 220,
          lowPassOctaves: 3.6,
          drive: 0.28,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'kick-click-body',
          slot: 1,
          waveform: 'triangle',
          frequency: 190,
          decay: 0.035,
          release: 0.01,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 320, resonance: 1.2 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 8, decay: 0.02, curve: 0.8 }),
          drive: 0.2,
        }),
        { gainDB: -10 },
      ),
      libraryLayer(transientPatch({ slug: 'kick-click-body', slot: 2, highPass: 5000, decay: 0.003 }), { gainDB: -7 }),
    ],
    { tags: ['drums', 'layered', 'punchy'] },
  ),

  /**
   * A struck drum rather than a synthesised one: a small pitch bend, a lot of beater noise,
   * and a shell that is allowed to be heard.
   */
  librarySound(
    'kick-acoustic',
    'Acoustic Kick',
    'kick',
    'Acoustic bass drum. Woody beater on a real-sounding shell.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-acoustic',
          slot: 0,
          frequency: 62,
          dropSemitones: 14,
          dropSeconds: 0.05,
          dropCurve: 0.6,
          decay: 0.3,
          curve: 0.5,
          lowPass: 420,
          lowPassOctaves: 3.2,
          lowPassDecay: 0.05,
          noiseLevel: 0.25,
          drive: 0.15,
        }),
      ),
      libraryLayer(
        transientPatch({ slug: 'kick-acoustic', slot: 1, highPass: 2200, decay: 0.012, toneHz: 3000, toneLevel: 0.35 }),
        { gainDB: -8 },
      ),
    ],
    { tags: ['drums', 'acoustic', 'live'] },
  ),

  librarySound(
    'kick-punch',
    'Punch Kick',
    'kick',
    'Punchy kick. Forward in the mids so it cuts through on small speakers.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-punch',
          slot: 0,
          frequency: 54,
          dropSemitones: 28,
          dropSeconds: 0.025,
          decay: 0.26,
          lowPass: 500,
          lowPassOctaves: 3.6,
          harmonic: { waveform: 'triangle', semitones: 12, level: 0.18 },
          drive: 0.4,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-punch', slot: 1, highPass: 3800, decay: 0.004 }), { gainDB: -9 }),
    ],
    { tags: ['drums', 'punchy', 'pop'] },
  ),

  librarySound(
    'kick-boombap',
    'Boom Bap Kick',
    'kick',
    'Boom bap kick. Round, slightly gritty, sitting back in the mix.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-boombap',
          slot: 0,
          frequency: 56,
          dropSemitones: 20,
          dropSeconds: 0.04,
          decay: 0.34,
          lowPass: 260,
          lowPassOctaves: 3.6,
          noiseLevel: 0.2,
          drive: 0.3,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-boombap', slot: 1, highPass: 1800, decay: 0.009 }), { gainDB: -11 }),
    ],
    {
      tags: ['drums', 'hip-hop', 'lo-fi'],
      post: libraryChain('kick-boombap', [
        effect('bitCrush', { bits: 11, mix: 0.35 }),
        effect('lowPass', { frequency: 7500, resonance: 0 }),
        effect('compressor', { threshold: -9, ratio: 2.5, attack: 0.008, release: 0.12, makeup: 2 }),
      ]),
    },
  ),

  /** Long, saturated, and given just enough room to rumble into the next bar. */
  librarySound(
    'kick-techno',
    'Techno Kick',
    'kick',
    'Techno kick. Long and saturated with a rumbling tail.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-techno',
          slot: 0,
          frequency: 46,
          dropSemitones: 26,
          dropSeconds: 0.05,
          decay: 0.42,
          curve: 0.4,
          lowPass: 220,
          lowPassOctaves: 4.0,
          drive: 0.55,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-techno', slot: 1, highPass: 3000, decay: 0.005 }), { gainDB: -13 }),
    ],
    {
      tags: ['drums', 'techno', 'long'],
      // Small and heavily damped. A bigger room measured 950 ms long, which is no longer a
      // kick — it is a kick and a problem for whatever plays on the next eighth.
      post: libraryChain('kick-techno', [
        effect('distortion', { drive: 0.35, tone: 0.5, character: 0, mix: 0.6 }),
        effect('reverb', { size: 0.18, damping: 0.8, preDelay: 0.004, mix: 0.1 }),
      ]),
    },
  ),

  librarySound(
    'kick-soft',
    'Soft Kick',
    'kick',
    'Soft kick. Rounded, with barely any click, for quiet and chilled beats.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-soft',
          slot: 0,
          frequency: 50,
          dropSemitones: 16,
          dropSeconds: 0.06,
          dropCurve: 0.5,
          decay: 0.3,
          curve: 0.45,
          lowPass: 150,
          lowPassOctaves: 2.6,
          drive: 0.1,
        }),
      ),
      libraryLayer(transientPatch({ slug: 'kick-soft', slot: 1, highPass: 1500, decay: 0.008 }), { gainDB: -20 }),
    ],
    { tags: ['drums', 'soft', 'chill'] },
  ),

  /**
   * The knock layer sits a couple of milliseconds late on purpose: the ear reads the click as
   * the hit and the knock as the drum behind it, which is what makes a small speaker report a
   * kick it cannot actually reproduce.
   */
  librarySound(
    'kick-knock',
    'Knock Kick',
    'kick',
    'Knock kick. A hard mid-range thud over a short sub.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'kick-knock',
          slot: 0,
          frequency: 60,
          dropSemitones: 24,
          dropSeconds: 0.03,
          decay: 0.22,
          lowPass: 700,
          lowPassOctaves: 3.0,
          drive: 0.35,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'kick-knock',
          slot: 1,
          waveform: 'sine',
          frequency: 170,
          decay: 0.06,
          release: 0.02,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 900, resonance: 1.4 }),
          drive: 0.3,
        }),
        { gainDB: -4, delay: 0.002 },
      ),
      libraryLayer(transientPatch({ slug: 'kick-knock', slot: 2, highPass: 4500, decay: 0.003 }), { gainDB: -10 }),
    ],
    { tags: ['drums', 'knock', 'trap'] },
  ),
];

// MARK: - Snares

export const snares: LibrarySound[] = [
  /**
   * Noise over a tuned shell, which is the whole anatomy of a snare. The two triangles a
   * fifth apart give it a pitch without letting it become a tom.
   */
  librarySound(
    'snare-trap',
    'Trap Snare',
    'snare',
    'Trap snare. Bright, tight noise over a short tuned body.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-trap',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1900,
          resonance: 0.9,
          envelopeOctaves: 1.4,
          envelopeDecay: 0.05,
          decay: 0.13,
          release: 0.03,
          drive: 0.2,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-trap',
          slot: 1,
          waveform: 'triangle',
          frequency: 190,
          partial: { semitones: 7, level: 0.5 },
          decay: 0.08,
          release: 0.02,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 2000, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -7 },
      ),
    ],
    { tags: ['drums', 'trap', 'tight'] },
  ),

  /**
   * A snare and a clap on the same pad, which is what most modern backbeats actually are. The
   * clap bursts start 4 ms late so the snare's transient still reads first.
   */
  librarySound(
    'snare-clap-layer',
    'Clap Snare',
    'snare',
    'Clap-snare. A snare with a hand clap stacked on top of it.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-clap-layer',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1700,
          resonance: 0.8,
          envelopeOctaves: 1.2,
          envelopeDecay: 0.05,
          decay: 0.11,
          release: 0.03,
          drive: 0.18,
        }),
      ),
      libraryLayer(
        noisePatch({
          slug: 'snare-clap-layer',
          slot: 1,
          filterType: 'bandPass',
          cutoff: 1150,
          resonance: 1.1,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          decay: 0.007,
          sustain: 0.2,
          release: 0.14,
          curve: 0.78,
          burstCount: 3,
          burstInterval: 0.009,
          drive: 0.15,
        }),
        { gainDB: -3, delay: 0.004 },
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-clap-layer',
          slot: 2,
          waveform: 'triangle',
          frequency: 185,
          partial: { semitones: 7, level: 0.45 },
          decay: 0.07,
          release: 0.02,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1800, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -9 },
      ),
    ],
    { tags: ['drums', 'layered', 'clap'] },
  ),

  librarySound(
    'snare-acoustic',
    'Acoustic Snare',
    'snare',
    'Acoustic snare. Full-bodied wires and shell with a small room around it.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-acoustic',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1400,
          resonance: 0.5,
          envelopeOctaves: 1.1,
          envelopeDecay: 0.08,
          decay: 0.2,
          release: 0.05,
          curve: 0.6,
          drive: 0.15,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-acoustic',
          slot: 1,
          waveform: 'triangle',
          frequency: 180,
          partial: { semitones: 7, level: 0.55 },
          decay: 0.13,
          release: 0.04,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1600, resonance: 1.2 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 3, decay: 0.03, curve: 0.7 }),
          drive: 0.18,
        }),
        { gainDB: -5 },
      ),
    ],
    {
      tags: ['drums', 'acoustic', 'live'],
      post: libraryChain('snare-acoustic', [
        effect('reverb', { size: 0.3, damping: 0.55, preDelay: 0.008, mix: 0.18 }),
      ]),
    },
  ),

  librarySound(
    'snare-rim',
    'Rim Snare',
    'snare',
    'Rim-heavy snare. Mostly wood and stick with a little wire behind it.',
    [
      libraryLayer(
        tonePatch({
          slug: 'snare-rim',
          slot: 0,
          waveform: 'square',
          frequency: 1650,
          partial: { semitones: 5, level: 0.6 },
          decay: 0.03,
          release: 0.01,
          curve: 0.85,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 1900, resonance: 3.0 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 6, decay: 0.006, curve: 0.9 }),
          drive: 0.3,
        }),
      ),
      libraryLayer(
        noisePatch({
          slug: 'snare-rim',
          slot: 1,
          filterType: 'bandPass',
          cutoff: 2400,
          resonance: 0.7,
          decay: 0.055,
          release: 0.02,
          drive: 0.15,
        }),
        { gainDB: -8 },
      ),
    ],
    { tags: ['drums', 'rim', 'woody'] },
  ),

  librarySound(
    'snare-lofi',
    'Lo-Fi Snare',
    'snare',
    'Lo-fi snare. Crushed and filtered, like an old sampler recorded it.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-lofi',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1500,
          resonance: 0.6,
          envelopeOctaves: 1.0,
          envelopeDecay: 0.05,
          decay: 0.14,
          release: 0.04,
          drive: 0.18,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-lofi',
          slot: 1,
          waveform: 'triangle',
          frequency: 175,
          partial: { semitones: 7, level: 0.5 },
          decay: 0.09,
          release: 0.02,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1500, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -6 },
      ),
    ],
    {
      tags: ['drums', 'lo-fi', 'dusty'],
      post: libraryChain('snare-lofi', [
        effect('bitCrush', { bits: 8, mix: 0.6 }),
        effect('sampleRateReduce', { rate: 12000, mix: 0.55 }),
        effect('lowPass', { frequency: 5500, resonance: 0 }),
      ]),
    },
  ),

  librarySound(
    'snare-tight',
    'Tight Snare',
    'snare',
    'Tight snare. Very short and dry, good for fast rolls.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-tight',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 2100,
          resonance: 1.0,
          envelopeOctaves: 1.3,
          envelopeDecay: 0.03,
          decay: 0.055,
          release: 0.015,
          curve: 0.8,
          drive: 0.2,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-tight',
          slot: 1,
          waveform: 'triangle',
          frequency: 210,
          partial: { semitones: 7, level: 0.45 },
          decay: 0.035,
          release: 0.01,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 2200, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -8 },
      ),
    ],
    { tags: ['drums', 'tight', 'short'] },
  ),

  /**
   * Two independent noise layers panned hard apart. They share a recipe but not a seed, so
   * the two sides are uncorrelated — which is what stereo width actually is. A single noise
   * layer panned wide is still mono, however wide the pan control is set.
   */
  librarySound(
    'snare-wide',
    'Wide Snare',
    'snare',
    'Wide snare. Spread across the stereo image with a long reverb tail.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-wide',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1650,
          resonance: 0.7,
          envelopeOctaves: 1.1,
          envelopeDecay: 0.06,
          decay: 0.16,
          release: 0.04,
          drive: 0.16,
        }),
        { pan: -0.55 },
      ),
      libraryLayer(
        noisePatch({
          slug: 'snare-wide',
          slot: 1,
          filterType: 'bandPass',
          cutoff: 1750,
          resonance: 0.7,
          envelopeOctaves: 1.1,
          envelopeDecay: 0.06,
          decay: 0.16,
          release: 0.04,
          drive: 0.16,
        }),
        { pan: 0.55 },
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-wide',
          slot: 2,
          waveform: 'triangle',
          frequency: 185,
          partial: { semitones: 7, level: 0.5 },
          decay: 0.1,
          release: 0.03,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1800, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -6 },
      ),
    ],
    {
      tags: ['drums', 'wide', 'reverb'],
      post: libraryChain('snare-wide', [effect('reverb', { size: 0.45, damping: 0.4, preDelay: 0.014, mix: 0.22 })]),
    },
  ),

  librarySound(
    'snare-fat',
    'Fat Snare',
    'snare',
    'Fat snare. Thick body, plenty of weight under the wires.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-fat',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1150,
          resonance: 0.6,
          envelopeOctaves: 1.2,
          envelopeDecay: 0.07,
          decay: 0.19,
          release: 0.05,
          curve: 0.62,
          drive: 0.22,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-fat',
          slot: 1,
          waveform: 'triangle',
          frequency: 165,
          partial: { semitones: 7, level: 0.6 },
          decay: 0.14,
          release: 0.04,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1400, resonance: 1.3 }),
          drive: 0.25,
        }),
        { gainDB: -3 },
      ),
    ],
    { tags: ['drums', 'fat', 'hip-hop'] },
  ),

  librarySound(
    'snare-rimshot',
    'Rimshot',
    'snare',
    'Rimshot. A hard crack off the rim and the head at once.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-rimshot',
          slot: 0,
          filterType: 'highPass',
          cutoff: 3000,
          resonance: 0.9,
          decay: 0.045,
          release: 0.015,
          curve: 0.85,
          drive: 0.25,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-rimshot',
          slot: 1,
          waveform: 'square',
          frequency: 420,
          partial: { semitones: 8, level: 0.5 },
          decay: 0.02,
          release: 0.008,
          curve: 0.9,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 800, resonance: 2.0 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 10, decay: 0.008, curve: 0.9 }),
          drive: 0.35,
        }),
        { gainDB: -4 },
      ),
    ],
    { tags: ['drums', 'rim', 'crack'] },
  ),

  /**
   * The only snare with a slow attack. A brush is dragged, not struck, and the envelope is
   * the only thing that says so.
   */
  librarySound(
    'snare-brush',
    'Brush Snare',
    'snare',
    'Brush snare. Soft and swept rather than struck.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-brush',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 2600,
          resonance: 0.4,
          envelopeOctaves: 0.6,
          envelopeDecay: 0.1,
          attack: 0.006,
          hold: 0.004,
          decay: 0.18,
          release: 0.06,
          curve: 0.25,
          drive: 0.08,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-brush',
          slot: 1,
          waveform: 'triangle',
          frequency: 200,
          partial: { semitones: 7, level: 0.4 },
          attack: 0.004,
          decay: 0.09,
          release: 0.03,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1400, resonance: 0.9 }),
          drive: 0.1,
        }),
        { gainDB: -12 },
      ),
    ],
    { tags: ['drums', 'brush', 'soft'] },
  ),

  librarySound(
    'snare-piccolo',
    'Piccolo Snare',
    'snare',
    'Piccolo snare. High-tuned and cracking, cuts over a busy mix.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-piccolo',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 2700,
          resonance: 0.8,
          envelopeOctaves: 1.2,
          envelopeDecay: 0.04,
          decay: 0.085,
          release: 0.025,
          curve: 0.78,
          drive: 0.2,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-piccolo',
          slot: 1,
          waveform: 'triangle',
          frequency: 265,
          partial: { semitones: 7, level: 0.45 },
          decay: 0.05,
          release: 0.015,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 2600, resonance: 1.0 }),
          drive: 0.22,
        }),
        { gainDB: -7 },
      ),
    ],
    { tags: ['drums', 'high', 'tight'] },
  ),

  librarySound(
    'snare-deep',
    'Deep Snare',
    'snare',
    'Deep snare. Low-tuned and dark, with a long wire decay.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-deep',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 950,
          resonance: 0.55,
          envelopeOctaves: 1.3,
          envelopeDecay: 0.08,
          decay: 0.24,
          release: 0.06,
          curve: 0.55,
          drive: 0.18,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-deep',
          slot: 1,
          waveform: 'triangle',
          frequency: 150,
          partial: { semitones: 7, level: 0.55 },
          decay: 0.16,
          release: 0.05,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 1200, resonance: 1.2 }),
          drive: 0.2,
        }),
        { gainDB: -4 },
      ),
    ],
    { tags: ['drums', 'deep', 'low'] },
  ),

  /**
   * Two bursts 5 ms apart. That doubling is the "snap" a modern trap snare has and a single
   * burst never gets, however bright it is filtered.
   */
  librarySound(
    'snare-snap',
    'Snap Snare',
    'snare',
    'Snap snare. Two very quick bursts that read as one sharp crack.',
    [
      libraryLayer(
        noisePatch({
          slug: 'snare-snap',
          slot: 0,
          filterType: 'highPass',
          cutoff: 2400,
          resonance: 0.9,
          envelopeOctaves: -0.4,
          envelopeDecay: 0.03,
          decay: 0.05,
          release: 0.02,
          curve: 0.85,
          burstCount: 2,
          burstInterval: 0.005,
          drive: 0.25,
        }),
      ),
      libraryLayer(
        tonePatch({
          slug: 'snare-snap',
          slot: 1,
          waveform: 'triangle',
          frequency: 230,
          partial: { semitones: 7, level: 0.4 },
          decay: 0.04,
          release: 0.012,
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 2400, resonance: 1.0 }),
          drive: 0.2,
        }),
        { gainDB: -10 },
      ),
    ],
    { tags: ['drums', 'snap', 'trap'] },
  ),
];

// MARK: - Hi-hats

export const hats: LibrarySound[] = [
  librarySound(
    'hat-closed-tight',
    'Closed Hat Tight',
    'hat',
    'Tight closed hi-hat. A very short bright tick.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-closed-tight',
          slot: 0,
          filterType: 'highPass',
          cutoff: 8000,
          resonance: 0.8,
          envelopeOctaves: -0.45,
          envelopeDecay: 0.02,
          decay: 0.028,
          release: 0.01,
          curve: 0.8,
          metalLevel: 0.2,
          metalBase: 6800,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'tight'] },
  ),

  librarySound(
    'hat-closed-loose',
    'Closed Hat Loose',
    'hat',
    'Loose closed hi-hat. Slightly longer, with the cymbals not quite shut.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-closed-loose',
          slot: 0,
          filterType: 'highPass',
          cutoff: 7200,
          resonance: 0.8,
          envelopeOctaves: -0.5,
          envelopeDecay: 0.03,
          decay: 0.055,
          release: 0.02,
          curve: 0.75,
          metalLevel: 0.24,
          metalBase: 6400,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'loose'] },
  ),

  librarySound(
    'hat-closed-dark',
    'Closed Hat Dark',
    'hat',
    'Dark closed hi-hat. Duller metal, sits behind the beat.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-closed-dark',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 5600,
          resonance: 0.6,
          envelopeOctaves: 0.5,
          envelopeDecay: 0.02,
          decay: 0.04,
          release: 0.015,
          curve: 0.78,
          metalLevel: 0.28,
          metalBase: 5200,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'dark'] },
  ),

  librarySound(
    'hat-closed-bright',
    'Closed Hat Bright',
    'hat',
    'Bright closed hi-hat. Thin, glassy and forward.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-closed-bright',
          slot: 0,
          filterType: 'highPass',
          cutoff: 10000,
          resonance: 0.9,
          envelopeOctaves: -0.3,
          envelopeDecay: 0.015,
          decay: 0.03,
          release: 0.01,
          curve: 0.82,
          metalLevel: 0.16,
          metalBase: 8200,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'bright'] },
  ),

  librarySound(
    'hat-half-open',
    'Half-Open Hat',
    'hat',
    'Half-open hi-hat. Between a tick and a ring.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-half-open',
          slot: 0,
          filterType: 'highPass',
          cutoff: 7000,
          resonance: 0.8,
          envelopeOctaves: -0.5,
          envelopeDecay: 0.04,
          decay: 0.11,
          release: 0.022,
          curve: 0.72,
          metalLevel: 0.26,
          metalBase: 6400,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'half-open'] },
  ),

  librarySound(
    'hat-open-short',
    'Open Hat Short',
    'hat',
    'Short open hi-hat. Rings for a fifth of a second and stops.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-open-short',
          slot: 0,
          filterType: 'highPass',
          cutoff: 6800,
          resonance: 0.8,
          envelopeOctaves: -0.5,
          envelopeDecay: 0.06,
          decay: 0.16,
          sustain: 0.03,
          release: 0.04,
          curve: 0.7,
          metalLevel: 0.28,
          metalBase: 6400,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'open'] },
  ),

  librarySound(
    'hat-open-long',
    'Open Hat Long',
    'hat',
    'Long open hi-hat. Left to ring out over half a second.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-open-long',
          slot: 0,
          filterType: 'highPass',
          cutoff: 6200,
          resonance: 0.8,
          envelopeOctaves: -0.55,
          envelopeDecay: 0.12,
          decay: 0.42,
          sustain: 0.04,
          release: 0.06,
          curve: 0.68,
          metalLevel: 0.3,
          metalBase: 6400,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'open', 'long'] },
  ),

  librarySound(
    'hat-pedal',
    'Pedal Hat',
    'hat',
    'Pedal hi-hat. The soft clap of the cymbals closing under the foot.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-pedal',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 4200,
          resonance: 0.7,
          envelopeOctaves: 0.6,
          envelopeDecay: 0.03,
          attack: 0.001,
          decay: 0.07,
          release: 0.02,
          curve: 0.6,
          metalLevel: 0.18,
          metalBase: 4600,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'pedal'] },
  ),

  librarySound(
    'hat-shaker',
    'Shaker',
    'hat',
    'Shaker. A handful of beads swept rather than struck.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-shaker',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 5200,
          resonance: 0.5,
          envelopeOctaves: 0.8,
          envelopeDecay: 0.05,
          attack: 0.004,
          hold: 0.004,
          decay: 0.09,
          release: 0.025,
          curve: 0.2,
          drive: 0.08,
        }),
      ),
    ],
    { tags: ['drums', 'shaker', 'percussion'] },
  ),

  /**
   * More metal than noise, and long: a ride is a struck bell that keeps ringing, so the two
   * detuned squares carry the sound and the noise is only the stick landing on it.
   */
  librarySound(
    'hat-ride',
    'Ride Cymbal',
    'hat',
    'Ride cymbal. A metallic ping that rings on underneath the beat.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-ride',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 3600,
          resonance: 0.9,
          envelopeOctaves: 1.0,
          envelopeDecay: 0.08,
          decay: 0.55,
          sustain: 0.05,
          release: 0.12,
          curve: 0.6,
          metalLevel: 0.55,
          metalBase: 3200,
          drive: 0.15,
        }),
      ),
    ],
    { tags: ['drums', 'ride', 'cymbal', 'long'] },
  ),

  librarySound(
    'hat-pitched-high',
    'Hat High',
    'hat',
    'High-pitched hi-hat. The same hat tuned up for a hat roll.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-pitched-high',
          slot: 0,
          filterType: 'highPass',
          cutoff: 9500,
          resonance: 0.9,
          envelopeOctaves: -0.35,
          envelopeDecay: 0.018,
          decay: 0.035,
          release: 0.012,
          curve: 0.8,
          metalLevel: 0.22,
          metalBase: 9000,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'pitched'] },
  ),

  librarySound(
    'hat-pitched-low',
    'Hat Low',
    'hat',
    'Low-pitched hi-hat. The same hat tuned down, heavier and duller.',
    [
      libraryLayer(
        noisePatch({
          slug: 'hat-pitched-low',
          slot: 0,
          filterType: 'highPass',
          cutoff: 5200,
          resonance: 0.8,
          envelopeOctaves: -0.5,
          envelopeDecay: 0.03,
          decay: 0.05,
          release: 0.016,
          curve: 0.75,
          metalLevel: 0.3,
          metalBase: 4200,
        }),
      ),
    ],
    { tags: ['drums', 'hi-hat', 'closed', 'pitched'] },
  ),
];

// MARK: - Claps

export const claps: LibrarySound[] = [
  /**
   * One pair of hands. The decay has to be far shorter than the burst interval or the repeats
   * smear into one long noise hit instead of reading as separate claps.
   */
  librarySound(
    'clap-single',
    'Clap',
    'clap',
    'Hand clap. One pair of hands with a short room tail.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-single',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1100,
          resonance: 1.0,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.012,
          sustain: 0.24,
          release: 0.13,
          curve: 0.75,
          drive: 0.15,
        }),
      ),
    ],
    { tags: ['drums', 'clap', 'single'] },
  ),

  /**
   * A room clapping: four hands on the front, two more a beat behind them. The second layer
   * is what turns a clap into a crowd.
   */
  librarySound(
    'clap-stacked',
    'Stacked Clap',
    'clap',
    'Stacked clap. Several pairs of hands a few milliseconds apart.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-stacked',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1150,
          resonance: 1.0,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.008,
          sustain: 0.16,
          release: 0.18,
          curve: 0.78,
          burstCount: 4,
          burstInterval: 0.009,
          drive: 0.15,
        }),
      ),
      libraryLayer(
        noisePatch({
          slug: 'clap-stacked',
          slot: 1,
          filterType: 'bandPass',
          cutoff: 1450,
          resonance: 0.9,
          envelopeOctaves: 0.7,
          envelopeDecay: 0.05,
          attack: 0.0006,
          hold: 0.0015,
          decay: 0.008,
          sustain: 0.12,
          release: 0.14,
          curve: 0.78,
          burstCount: 2,
          burstInterval: 0.011,
          drive: 0.12,
        }),
        { gainDB: -5, delay: 0.022 },
      ),
    ],
    { tags: ['drums', 'clap', 'layered'] },
  ),

  librarySound(
    'clap-wide',
    'Wide Clap',
    'clap',
    'Wide clap. Two uncorrelated claps hard left and right.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-wide',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1100,
          resonance: 1.0,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.009,
          sustain: 0.22,
          release: 0.14,
          curve: 0.77,
          burstCount: 3,
          burstInterval: 0.0085,
          drive: 0.15,
        }),
        { pan: -0.7 },
      ),
      libraryLayer(
        noisePatch({
          slug: 'clap-wide',
          slot: 1,
          filterType: 'bandPass',
          cutoff: 1250,
          resonance: 1.0,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.009,
          sustain: 0.22,
          release: 0.14,
          curve: 0.77,
          burstCount: 3,
          burstInterval: 0.0095,
          drive: 0.15,
        }),
        { pan: 0.7 },
      ),
    ],
    { tags: ['drums', 'clap', 'wide', 'stereo'] },
  ),

  librarySound(
    'clap-tight',
    'Tight Clap',
    'clap',
    'Tight clap. Three quick bursts and no room at all.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-tight',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1600,
          resonance: 1.2,
          envelopeOctaves: 0.8,
          envelopeDecay: 0.03,
          attack: 0.0004,
          hold: 0.001,
          decay: 0.006,
          sustain: 0.12,
          release: 0.055,
          curve: 0.82,
          burstCount: 3,
          burstInterval: 0.007,
          drive: 0.18,
        }),
      ),
    ],
    { tags: ['drums', 'clap', 'tight'] },
  ),

  librarySound(
    'clap-reverb',
    'Reverb Clap',
    'clap',
    'Reverb clap. A big clap in a big room.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-reverb',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1200,
          resonance: 1.0,
          envelopeOctaves: 0.9,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.009,
          sustain: 0.22,
          release: 0.13,
          curve: 0.77,
          burstCount: 3,
          burstInterval: 0.009,
          drive: 0.18,
        }),
      ),
    ],
    {
      tags: ['drums', 'clap', 'reverb', 'big'],
      post: libraryChain('clap-reverb', [effect('reverb', { size: 0.45, damping: 0.45, preDelay: 0.018, mix: 0.18 })]),
    },
  ),

  librarySound(
    'clap-lofi',
    'Lo-Fi Clap',
    'clap',
    'Lo-fi clap. Crushed and dulled, like an old drum machine.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-lofi',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 1300,
          resonance: 0.9,
          envelopeOctaves: 0.8,
          envelopeDecay: 0.05,
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.009,
          sustain: 0.2,
          release: 0.12,
          curve: 0.77,
          burstCount: 3,
          burstInterval: 0.01,
          drive: 0.15,
        }),
      ),
    ],
    {
      tags: ['drums', 'clap', 'lo-fi'],
      post: libraryChain('clap-lofi', [
        effect('bitCrush', { bits: 8, mix: 0.6 }),
        effect('lowPass', { frequency: 5000, resonance: 0 }),
      ]),
    },
  ),

  librarySound(
    'clap-snap',
    'Finger Snap',
    'clap',
    'Finger snap. One dry, high click of skin on skin.',
    [
      libraryLayer(
        noisePatch({
          slug: 'clap-snap',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 2600,
          resonance: 1.6,
          envelopeOctaves: 1.0,
          envelopeDecay: 0.02,
          attack: 0.0004,
          hold: 0.001,
          decay: 0.008,
          sustain: 0.1,
          release: 0.05,
          curve: 0.85,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['drums', 'snap', 'acoustic'] },
  ),
];

// MARK: - Percussion

export const percussion: LibrarySound[] = [
  librarySound(
    'perc-rim',
    'Rim',
    'percussion',
    'Rim click. A dry wooden tap with a hint of pitch in it.',
    [
      libraryLayer(
        tonePatch({
          slug: 'perc-rim',
          slot: 0,
          waveform: 'square',
          frequency: 1720,
          partial: { semitones: 5, level: 0.7 },
          decay: 0.03,
          release: 0.008,
          curve: 0.85,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 1900, resonance: 3.0 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 6, decay: 0.006, curve: 0.9 }),
          noiseLevel: 0.25,
          drive: 0.3,
        }),
      ),
    ],
    { tags: ['percussion', 'rim', 'woody'] },
  ),

  /**
   * The interval is the whole trick: 6.78 semitones is deliberately not a musical interval,
   * and tuning the two squares to anything consonant stops it being a cowbell.
   */
  librarySound(
    'perc-cowbell',
    'Cowbell',
    'percussion',
    'Cowbell. Two clashing tones that stop quickly.',
    [
      libraryLayer(
        tonePatch({
          slug: 'perc-cowbell',
          slot: 0,
          waveform: 'square',
          frequency: 540,
          partial: { semitones: 6.78, level: 0.85 },
          decay: 0.075,
          sustain: 0.26,
          release: 0.15,
          curve: 0.7,
          filter: makeSynthFilter({
            type: 'bandPass',
            cutoff: 1200,
            resonance: 1.4,
            envelopeAmount: 0.5,
            envelope: makeSynthEnvelope({
              attack: 0.0006,
              decay: 0.08,
              sustain: 0.15,
              release: 0.2,
              curve: 0.7,
            }),
          }),
          drive: 0.45,
        }),
      ),
    ],
    { tags: ['percussion', 'cowbell', 'metal'] },
  ),

  librarySound(
    'perc-tom-high',
    'Tom High',
    'percussion',
    'High tom. A bright drum tone that bends down as it fades.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'perc-tom-high',
          slot: 0,
          frequency: 200,
          dropSemitones: 8,
          dropSeconds: 0.09,
          dropCurve: 0.7,
          decay: 0.3,
          curve: 0.6,
          lowPass: 900,
          lowPassOctaves: 2.4,
          lowPassDecay: 0.04,
          noiseLevel: 0.2,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['percussion', 'tom', 'high'] },
  ),

  librarySound(
    'perc-tom-mid',
    'Tom Mid',
    'percussion',
    'Mid tom. A rounded drum tone falling in pitch.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'perc-tom-mid',
          slot: 0,
          frequency: 140,
          dropSemitones: 8,
          dropSeconds: 0.11,
          dropCurve: 0.7,
          decay: 0.36,
          curve: 0.6,
          lowPass: 700,
          lowPassOctaves: 2.4,
          lowPassDecay: 0.05,
          noiseLevel: 0.2,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['percussion', 'tom', 'mid'] },
  ),

  librarySound(
    'perc-tom-low',
    'Tom Low',
    'percussion',
    'Low tom. A deep drum tone with a long bend downward.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'perc-tom-low',
          slot: 0,
          frequency: 95,
          dropSemitones: 9,
          dropSeconds: 0.14,
          dropCurve: 0.7,
          decay: 0.45,
          curve: 0.6,
          lowPass: 520,
          lowPassOctaves: 2.4,
          lowPassDecay: 0.06,
          noiseLevel: 0.18,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['percussion', 'tom', 'low'] },
  ),

  librarySound(
    'perc-conga-high',
    'Conga High',
    'percussion',
    'High conga. An open hand slap on a small hand drum.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'perc-conga-high',
          slot: 0,
          frequency: 260,
          dropSemitones: 5,
          dropSeconds: 0.05,
          dropCurve: 0.75,
          decay: 0.2,
          curve: 0.7,
          lowPass: 1600,
          lowPassOctaves: 2.0,
          lowPassDecay: 0.03,
          harmonic: { waveform: 'triangle', semitones: 12, level: 0.22 },
          noiseLevel: 0.16,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['percussion', 'conga', 'hand'] },
  ),

  librarySound(
    'perc-conga-low',
    'Conga Low',
    'percussion',
    'Low conga. A deeper open tone from the larger drum.',
    [
      libraryLayer(
        bodyPatch({
          slug: 'perc-conga-low',
          slot: 0,
          frequency: 175,
          dropSemitones: 6,
          dropSeconds: 0.07,
          dropCurve: 0.75,
          decay: 0.28,
          curve: 0.7,
          lowPass: 1200,
          lowPassOctaves: 2.0,
          lowPassDecay: 0.04,
          harmonic: { waveform: 'triangle', semitones: 12, level: 0.2 },
          noiseLevel: 0.16,
          drive: 0.2,
        }),
      ),
    ],
    { tags: ['percussion', 'conga', 'hand'] },
  ),

  librarySound(
    'perc-woodblock',
    'Woodblock',
    'percussion',
    'Woodblock. A hard, hollow knock on a block of wood.',
    [
      libraryLayer(
        tonePatch({
          slug: 'perc-woodblock',
          slot: 0,
          waveform: 'square',
          frequency: 1180,
          partial: { semitones: 9.4, level: 0.6 },
          decay: 0.035,
          release: 0.01,
          curve: 0.88,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 1600, resonance: 2.5 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 4, decay: 0.005, curve: 0.9 }),
          drive: 0.25,
        }),
      ),
    ],
    { tags: ['percussion', 'wood', 'click'] },
  ),

  /**
   * A struck triangle is almost pure inharmonic ring, so the partial is deliberately a
   * quarter-tone off any interval and the decay runs long with nothing else under it.
   */
  librarySound(
    'perc-triangle',
    'Triangle',
    'percussion',
    'Triangle. A thin metallic ring that hangs in the air.',
    [
      libraryLayer(
        tonePatch({
          slug: 'perc-triangle',
          slot: 0,
          waveform: 'sine',
          frequency: 5200,
          partial: { semitones: 6.4, level: 0.8 },
          decay: 0.5,
          sustain: 0.15,
          release: 0.6,
          curve: 0.55,
          filter: makeSynthFilter({ type: 'highPass', cutoff: 3800, resonance: 0.8 }),
          noiseLevel: 0.05,
          drive: 0.1,
        }),
      ),
    ],
    { tags: ['percussion', 'metal', 'bell'] },
  ),

  librarySound(
    'perc-tambourine',
    'Tambourine',
    'percussion',
    'Tambourine. A jingle of small metal discs.',
    [
      libraryLayer(
        noisePatch({
          slug: 'perc-tambourine',
          slot: 0,
          filterType: 'bandPass',
          cutoff: 7000,
          resonance: 1.2,
          envelopeOctaves: 0.6,
          envelopeDecay: 0.04,
          attack: 0.0005,
          hold: 0.001,
          decay: 0.02,
          sustain: 0.1,
          release: 0.18,
          curve: 0.8,
          burstCount: 3,
          burstInterval: 0.006,
          metalLevel: 0.15,
          metalBase: 8000,
          drive: 0.12,
        }),
      ),
    ],
    { tags: ['percussion', 'tambourine', 'metal'] },
  ),

  librarySound(
    'perc-clave',
    'Clave',
    'percussion',
    'Clave. Two hardwood sticks struck together.',
    [
      libraryLayer(
        tonePatch({
          slug: 'perc-clave',
          slot: 0,
          waveform: 'sine',
          frequency: 2500,
          partial: { semitones: 7, level: 0.5 },
          decay: 0.05,
          release: 0.015,
          curve: 0.85,
          filter: makeSynthFilter({ type: 'bandPass', cutoff: 2500, resonance: 2.0 }),
          pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 3, decay: 0.004, curve: 0.9 }),
          noiseLevel: 0.12,
          drive: 0.3,
        }),
      ),
    ],
    { tags: ['percussion', 'wood', 'latin'] },
  ),

  librarySound(
    'perc-crash',
    'Crash',
    'percussion',
    'Crash cymbal. A bright wash of metal that takes over a second to fade.',
    [
      libraryLayer(
        noisePatch({
          slug: 'perc-crash',
          slot: 0,
          filterType: 'highPass',
          cutoff: 4500,
          resonance: 0.7,
          envelopeOctaves: -0.6,
          envelopeDecay: 0.2,
          attack: 0.001,
          hold: 0.004,
          decay: 1.1,
          sustain: 0.06,
          release: 0.4,
          curve: 0.55,
          metalLevel: 0.25,
          metalBase: 3800,
          drive: 0.12,
        }),
      ),
    ],
    { tags: ['percussion', 'cymbal', 'crash', 'long'] },
  ),
];
